import { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button } from '@nazka/nazka.mapframe.components';
import { normalizeText } from '@services/utils.service';

import { useVacancy, useVacancyFilterOptions } from '@services/query.service';
import { setUseJobApplicationLocation } from './job-vacancy.slice';
import { cleanJobDescription } from './job-vacancy.utils';

import './job-vacancy.styl';

function JobVacancy() {
  const { companyId, vacancyId } = useParams();
  const OIDN = companyId;
  const vdabReferentie = Number(vacancyId);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { 
    data: companyData,
  } = useVacancy(OIDN);

  const vacancyData = useMemo(() => {
    return companyData?.properties?.vacancies.find((v) => {
      return v?.properties?.vacatureReferentie?.vdabReferentie === vdabReferentie;
    })
  }, [companyData, vdabReferentie]);

  const {
    functie, vacatureReferentie, publicatieDatum, profiel, arbeidsovereenkomst,
  } = vacancyData?.properties || {};

  const { data: filterOptions, isLoading } = useVacancyFilterOptions();

  useEffect(() => {
    return () => {
      dispatch(setUseJobApplicationLocation(false));
    };
  }, [dispatch]);

  if (!vacancyData) {
    navigate('/');
  }

  return (
    <div className={`job-vacancy${isLoading ? ' is-loading' : ''}`}>
      <h3 className="job-vacancy__title">
        {normalizeText(functie?.functieTitel)}
      </h3>
      <p className="job-vacancy__subtitle">
        {`Online sinds ${new Date(publicatieDatum).toLocaleDateString('nl-BE')}`}
      </p>
      <div className="job-vacancy__feature">
        <h4 className="job-vacancy__feature__title">
          {`Functieomschrijving${functie?.aantalJobs && Number(functie.aantalJobs) > 1 ? ` (${functie.aantalJobs})` : ''}`}
        </h4>
        <div className="job-vacancy__feature__content">
          {functie?.beroepsprofiel?.label && (
            <p>{`${functie.beroepsprofiel.label}`}</p>
          )}
          {functie?.beroepsprofiel?.ervaring?.label && (
            <p>{`Vereiste ervaring: ${functie?.beroepsprofiel?.ervaring?.label}`}</p>
          )}
          <div 
            className="job-vacancy__feature__content__html"
            dangerouslySetInnerHTML={{ __html: cleanJobDescription(functie?.omschrijving) }}
          />
          <Button
            layout="ghost"
            themeColor="secondary"
            hover={false}
            className="job-vacancy__feature__content__more"
            href={`https://www.vdab.be/vindeenjob/vacatures/${vacatureReferentie?.vdabReferentie}`}
            rootElProps={{ 
              onClick: () => {
                window.gtag?.('event', 'vacancy_click_lees_meer_button', {
                  event_category: 'Vacancy',
                  event_label: 'Vacancy click "lees meer" button',
                  vdabReferentie: vacatureReferentie?.vdabReferentie,
                });
              },
              target: '_blank',
              rel: 'noreferrer',
            }}
          >
            Lees verder
          </Button>
        </div>
      </div>
      <div className="job-vacancy__feature">
        <h4 className="job-vacancy__feature__title">
          Contract
        </h4>
        <div className="job-vacancy__feature__content">
          <ul>
            {arbeidsovereenkomst?.arbeidsContract?.label && (
            <li>{arbeidsovereenkomst.arbeidsContract.label}</li>
            )}
            {arbeidsovereenkomst?.arbeidsContractDuur && (
            <li>
              {arbeidsovereenkomst.arbeidsContractDuur.charAt(0).toUpperCase() 
              + arbeidsovereenkomst.arbeidsContractDuur.slice(1).toLowerCase()}
            </li>
            )}
            {arbeidsovereenkomst?.arbeidsRegime?.label && (
            <li>
              {arbeidsovereenkomst.arbeidsRegime.label.charAt(0).toUpperCase() 
              + arbeidsovereenkomst.arbeidsRegime.label.slice(1).toLowerCase()}
            </li>
            )}
            {arbeidsovereenkomst?.arbeidsStelsel && arbeidsovereenkomst.arbeidsStelsel.map(as => (
              <li key={as.code}>{as?.label}</li>
            ))}
          </ul>
        </div>
      </div>
      <div className="job-vacancy__feature">
        <h4 className="job-vacancy__feature__title">
          Studies
        </h4>
        <div className="job-vacancy__feature__content">
          {filterOptions && profiel?.vereisten.find(v => v.type === 'studie') ? (
            <ul>
              {profiel.vereisten.filter(v => v.type === 'studie').map((studie) => {
                return (
                  <li key={studie.code}>
                    {studie?.label}
                  </li>
                )
              })}
            </ul>
          ) : (
            <p className="job-vacancy__feature__content">
              Geen specifieke studievereisten
            </p>
          )}
        </div>
      </div>
      <div className="job-vacancy__feature">
        <h4 className="job-vacancy__feature__title">
          Talenkennis
        </h4>
        <div className="job-vacancy__feature__content">
          {filterOptions && profiel?.vereisten.find(v => v.type === 'talen') ? (
            <ul>
              {profiel.vereisten.filter(v => v.type === 'talen').map((taal) => {
                return (
                  <li key={taal.code}>
                    {`${filterOptions.talen.find(d => d.code === taal.code)?.beschrijving}`}
                    {taal?.score?.label ? ` (${taal.score.label})` : ''}
                  </li>
                )
              })}
            </ul>
          ) : (
            <p className="job-vacancy__feature__content">
              Geen specifieke talenkennins nodig
            </p>
          )}
        </div>
      </div>
      <div className="job-vacancy__feature">
        <h4 className="job-vacancy__feature__title">
          Rijbewijs
        </h4>
        <div className="job-vacancy__feature__content">
          {filterOptions && profiel?.vereisten.find(v => v.type === 'rijbewijs') ? (
            <ul>
              {profiel.vereisten.filter(v => v.type === 'rijbewijs').map((rijbewijs) => {
                return filterOptions.rijbewijzen.find(d => d.code === rijbewijs.code)?.beschrijving;
              }).join(', ')}
            </ul>
          ) : (
            <p className="job-vacancy__feature__content">
              Geen rijbewijs nodig
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default JobVacancy;
